import { Result } from 'better-result';
import * as z from 'zod';
import { ValidationError } from '../result/app-error.ts';
import { finnest } from '../schemas/meta/registry.ts';

// Official Zod JSON-encodable value (zod.dev/json). Not a domain type — use
// this only for a tree walker or a payload that is then decoded by another
// schema. Prefer a domain schema at every decision seam. Do not pass
// `z.unknown()`; that is not a parsed type.
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };
// Keep the validated schema's output aligned with the repository's immutable
// JSON tree contract. Zod accepts ordinary mutable arrays and objects as input;
// callers receive the structurally readonly domain type.
export const JsonValue: z.ZodType<JsonValue> = z.json();

export type JsonObject = { readonly [key: string]: JsonValue };
export const JsonObject: z.ZodType<JsonObject> = z.record(z.string(), JsonValue);
finnest.add(JsonObject, {
  id: 'core.json.object',
  title: 'JSON object',
  description: 'A JSON object: string keys and JSON-encodable values.',
  version: 'v1',
});

// Zod v4's documented JSON-string codec (zod.dev/codecs). Decode is
// JSON.parse; a syntax error becomes a Zod `invalid_format` issue, then the
// output schema runs. The success type is the schema output, brands included.
export function jsonString<S extends z.ZodType>(schema: S): z.ZodCodec<z.ZodString, S> {
  return z.codec(z.string(), schema, {
    decode: (text, payload) => {
      const parsed = Result.try({
        try: () => JSON.parse(text),
        catch: (cause) => cause,
      });
      if (parsed.isErr()) {
        payload.issues.push({
          code: 'invalid_format',
          format: 'json',
          input: text,
          message: parsed.error instanceof Error ? parsed.error.message : 'invalid JSON',
        });
        return z.NEVER;
      }
      return parsed.value;
    },
    encode: (value) => JSON.stringify(value),
  });
}

export function parseJson<S extends z.ZodType>(
  text: string,
  schema: S,
): Result<z.output<S>, ValidationError> {
  const decoded = jsonString(schema).safeDecode(text);
  if (!decoded.success) {
    return Result.err(validationError(decoded.error.issues.map((issue) => issue.message)));
  }
  return Result.ok(decoded.data);
}

export function decodeJson<S extends z.ZodType>(
  value: unknown,
  schema: S,
): Result<z.output<S>, ValidationError> {
  const parsed = schema.safeParse(value);
  if (!parsed.success) {
    return Result.err(validationError(parsed.error.issues.map((issue) => issue.message)));
  }
  return Result.ok(parsed.data);
}

function validationError(messages: readonly string[]): ValidationError {
  return new ValidationError({ field: 'json', message: messages.join('; ') });
}
